import {
  AppstoreAddOutlined,
  DeleteOutlined,
  DoubleRightOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  FloatButton,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd"
import dayjs from "dayjs"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import { useRegulatoryRequest } from "../../components/en1176/useRegulatoryAnswers"
import {
  StorageEntityType,
  StorageRequest,
  UCPayloadType,
  UUID,
} from "../../generated/proto-ts/main"
import { useCloudUsercommSitesWS } from "../../usercomm/cloud/cloudUsercommAsyncRequestWS"

import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { Label } from "../../components/commons-ts/input"
import { SimplifiedBreadcrumb } from "../../components/commons-ts/simplifiedBreadcrumb"
import { TableImageThumbnail } from "../../components/commons-ts/tableImageThumbnail"
import { NormType, Site } from "../../generated/proto-ts/main"
import { Translated } from "../../utils/translated"
import {
  CENTRAL_CONTAINER_GAP,
  COLOR_BG_GRAY,
  COLOR_BG_ROW_DELETED,
  formatDayOrLongDash,
  pbUUIDToUuid,
  uuidToPbUUID,
} from "../../utils/utils"

import { TemplateSelector } from "../../components/en1176/CatalogAssignment"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useTranslationContext } from "../../providers/translationProvider"
import { ICloudCustomer } from "../../types"
import { useUsercommDeleteSiteBimodal } from "../../usercomm/common/usercommAsyncRequestBimodal"
import { callCloudApiV2 } from "../../utils/cloudApiV2"
import { normalizeString } from "../../utils/users"

export const HomePage_EN1176: FC = () => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const history = useHistory()
  const { translated } = useTranslationContext()
  const { isAdmin, currentUser, currentEnterprise } = useSyncronizationContext()

  const [sites, getSites] = useCloudUsercommSitesWS()
  const [deletedSiteAck, deleteSite] = useUsercommDeleteSiteBimodal()
  const { request: createRequest } = useRegulatoryRequest()

  const [searchQuery, setSearchQuery] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  // New Site Form State
  const [newSiteName, setNewSiteName] = useState<string>("")
  const [newClientName, setNewClientName] = useState<string>("")
  const [newMissionName, setNewMissionName] = useState<string>("")
  const [newAddress, setNewAddress] = useState<string>("")
  const [newExecutionDate, setNewExecutionDate] = useState<dayjs.Dayjs | null>(dayjs())
  const [isCreating, setIsCreating] = useState<boolean>(false)

  const [selectedRegulatoryTemplateUUID, setSelectedRegulatoryTemplateUUID] = useState<
    string | null
  >(null)

  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[]>([])

  useEffect(() => {
    getSites()
  }, [])

  // Cloud API Data (Enterprise: customers, users, templates)
  useEffect(() => {
    if (currentEnterprise === null) return

    callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${currentEnterprise.UUID}`,
    ).then(({ entity }) => {
      if (entity) setEnterpriseCustomers(entity)
    })
  }, [currentEnterprise])

  // Filter sites for EN 1176
  const en1176Sites = useMemo(() => {
    if (sites === null) return []
    return sites.filter((site) => {
      if (site.deleted_at > 0 && !isAdmin) return false
      if (site.norm_type !== NormType.EN_1176) return false
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase()
        const nameMatch = site.site_name.toLowerCase().includes(q)
        const clientMatch = site.client_name.toLowerCase().includes(q)
        const missionMatch = site.mission_name.toLowerCase().includes(q)
        return nameMatch || clientMatch || missionMatch
      }
      return true
    })
  }, [sites, isAdmin, searchQuery])

  // Handle New Site Creation
  const handleCreateSite = useCallback(async () => {
    if (!newSiteName) return
    if (!currentUser) {
      antdMessageCtx.error(translated("Vous devez être connecté pour créer un site."))
      return
    }
    if (!selectedRegulatoryTemplateUUID) {
      antdMessageCtx.error(translated("Vous devez sélectionner un modèle réglementaire."))
      return
    }
    setIsCreating(true)
    const ts = Date.now()
    const newSite = new Site({
      uuid: uuidToPbUUID(uuidv4()),
      user_uuid: uuidToPbUUID(currentUser.UUID),
      regulatory_template_uuid: uuidToPbUUID(selectedRegulatoryTemplateUUID),
      norm_type: NormType.EN_1176,
      site_name: newSiteName,
      client_name: newClientName,
      mission_name: newMissionName,
      address: newAddress,
      created_at: ts,
      updated_at: ts,
    })
    if (newExecutionDate) {
      newSite.execution_date = newExecutionDate.toDate().getTime()
    }
    try {
      const payload = await createRequest(
        UCPayloadType.SICO_STORAGE_CREATE_ENTITY,
        new StorageRequest({
          entity_type: StorageEntityType.SITE,
          parent_uuid: newSite.user_uuid,
          entity_uuid: newSite.uuid,
          single_entity_data: newSite.serializeBinary(),
        }),
      )
      const created = UUID.deserializeBinary(payload.data)
      antdMessageCtx.success("Site d’inspection créé.")
      setIsModalOpen(false)
      history.push(`/regulatory/sites/${pbUUIDToUuid(created)}`)
    } catch (reason) {
      antdMessageCtx.error(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setIsCreating(false)
    }
  }, [
    newSiteName,
    newClientName,
    newMissionName,
    newAddress,
    newExecutionDate,
    currentUser,
    selectedRegulatoryTemplateUUID,
  ])

  const onDeleteSite = useCallback(
    (siteUuid: Site["uuid"]) => {
      deleteSite(siteUuid)
    },
    [deleteSite],
  )

  useEffect(() => {
    if (deletedSiteAck) {
      antdMessageCtx.info(translated("Site supprimé !"))
      getSites()
    }
  }, [deletedSiteAck])

  const columns = useMemo(() => {
    return [
      {
        title: "📷",
        width: "3.5rem",
        render: (_: any, site: Site) => {
          const picUuid = site.pictures[0]
          if (!picUuid) return <div style={{ width: "3rem" }} />
          return (
            <Link to={`/regulatory/sites/${pbUUIDToUuid(site.uuid)}`}>
              <TableImageThumbnail pictureUUID={picUuid} alt={site.site_name} />
            </Link>
          )
        },
      },
      {
        title: <Translated keyEn="Site / Aire de jeux" />,
        dataIndex: "site_name",
        render: (name: string, site: Site) => (
          <Link
            to={`/regulatory/sites/${pbUUIDToUuid(site.uuid)}`}
            style={{ fontWeight: "bold", fontSize: "1.05rem", textDecoration: "none" }}
          >
            {name || "Sans nom"}
          </Link>
        ),
      },
      {
        title: <Translated keyEn="Client" />,
        dataIndex: "client_name",
        render: (client: string) => client || "—",
      },
      {
        title: <Translated keyEn="Mission" />,
        dataIndex: "mission_name",
        render: (mission: string) => mission || "—",
      },
      {
        title: <Translated keyEn="Date d'exécution" />,
        dataIndex: "execution_date",
        render: (date: number) => (date === 0 ? "—" : formatDayOrLongDash(date, true)),
      },
      {
        title: <Translated keyEn="Norme" />,
        render: () => <Tag color="purple">EN 1176</Tag>,
      },
      {
        title: "",
        width: "5rem",
        render: (_: any, site: Site) => (
          <FlexRow style={{ gap: 8 }}>
            <Popconfirm
              title={<Translated keyEn="Supprimer ce site ?" />}
              onConfirm={() => onDeleteSite(site.uuid)}
            >
              <Button danger type="link" size="small" icon={<DeleteOutlined />} />
            </Popconfirm>

            <Link to={`/regulatory/sites/${pbUUIDToUuid(site.uuid)}`}>
              <Button type="link" size="small">
                <DoubleRightOutlined />
              </Button>
            </Link>
          </FlexRow>
        ),
      },
    ]
  }, [])

  return (
    <>
      {contextHolder}
      <FlexCol
        className="regulatory-operational"
        style={{ width: "100%", gap: CENTRAL_CONTAINER_GAP }}
      >
        <SimplifiedBreadcrumb
          root="/"
          previousItems={[]}
          currentItem={{ label: "Contrôle d'Aires de Jeux (EN 1176)" }}
        />

        {/* Page Header */}
        <Row justify="space-between" align="middle" style={{ marginTop: "0.5rem" }}>
          <Col>
            <FlexCol style={{ gap: 4 }}>
              <Typography.Title level={2} style={{ margin: 0 }}>
                <Translated keyEn="Missions de Contrôle d'Aires de Jeux" />
              </Typography.Title>
              <span style={{ color: COLOR_BG_GRAY }}>
                <Translated keyEn="Gestion des inspections de sécurité selon les normes NF EN 1176" />
              </span>
            </FlexCol>
          </Col>
          {/* <Col>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
              style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
            >
              <Translated keyEn="Nouveau Site EN 1176" />
            </Button>
          </Col> */}
        </Row>

        {/* Search Bar */}
        <Input
          size="large"
          prefix={<SearchOutlined style={{ color: COLOR_BG_GRAY }} />}
          placeholder={translated("Rechercher un site, client ou mission EN 1176...") ?? ""}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Sites List Table */}
        <Table
          scroll={{ x: 760 }}
          dataSource={en1176Sites}
          columns={columns}
          rowKey={(s) => pbUUIDToUuid(s.uuid)}
          loading={sites === null}
          pagination={false}
          size="small"
          bordered
          onRow={(record) => {
            if (record.deleted_at === 0) return {}
            return isAdmin
              ? { style: { backgroundColor: COLOR_BG_ROW_DELETED } }
              : { style: { display: "none" } }
          }}
        />

        {/* Create Site Modal */}
        <Modal
          title={
            <Space>
              <SafetyCertificateOutlined style={{ color: "#722ed1" }} />
              <Translated keyEn="Créer une nouvelle mission d'inspection EN 1176" />
            </Space>
          }
          open={isModalOpen}
          onOk={handleCreateSite}
          onCancel={() => setIsModalOpen(false)}
          confirmLoading={isCreating}
          okText={<Translated keyEn="Créer le site" />}
          cancelText={<Translated keyEn="Annuler" />}
          okButtonProps={{ disabled: !newSiteName }}
        >
          <FlexCol style={{ gap: 12, paddingTop: 10 }}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Modèle d'inspection (Norme)" />
              </Label>
              <TemplateSelector
                scope="site"
                value={selectedRegulatoryTemplateUUID}
                onChange={setSelectedRegulatoryTemplateUUID}
              />
            </FlexCol>

            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Nom du site / Aire de jeux *" />
              </Label>
              <Input
                size="large"
                placeholder="Ex: Aire de jeux du Jardin des Plantes"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
              />
            </FlexCol>

            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Client / Exploitant" />
              </Label>
              <AutoComplete
                size="large"
                variant="filled"
                value={newClientName ?? ""}
                style={{ width: "100%" }}
                options={enterpriseCustomers.map((customer) => ({ value: customer.Name }))}
                onChange={(value) => {
                  setNewClientName(value)
                }}
                onSelect={(value) => {
                  let selectedCustomer = enterpriseCustomers.find((c) => c.Name === value)
                  if (selectedCustomer) {
                    setNewClientName(selectedCustomer.Name)
                  }
                }}
                filterOption={(inputValue, option) =>
                  normalizeString(option!.value).indexOf(normalizeString(inputValue)) !== -1
                }
              />
            </FlexCol>

            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Nom de la mission" />
              </Label>
              <Input
                size="large"
                placeholder="Ex: Inspection Annuelle 2026"
                value={newMissionName}
                onChange={(e) => setNewMissionName(e.target.value)}
              />
            </FlexCol>

            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Adresse" />
              </Label>
              <Input
                size="large"
                placeholder="Adresse ou commune"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
              />
            </FlexCol>

            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Date d'exécution" />
              </Label>
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                value={newExecutionDate}
                onChange={(val) => setNewExecutionDate(val)}
              />
            </FlexCol>
          </FlexCol>
        </Modal>
        <Link to="/regulatory/templates">
          <FloatButton
            type="default"
            icon={<AppstoreAddOutlined />}
            style={{
              width: 60,
              height: 60,
              right: "5vw",
              bottom: 130,
              backgroundColor: "#722ed1",
              borderColor: "#722ed1",
            }}
            tooltip={<Translated keyEn="Modèles de questionnaire" />}
          />
        </Link>
        <FloatButton
          type="primary"
          style={{
            width: 60,
            height: 60,
            right: "5vw",
            bottom: 60,
            backgroundColor: "#722ed1",
            borderColor: "#722ed1",
          }}
          onClick={() => {
            setIsModalOpen(true)
          }}
          description={
            <Tooltip title={<Translated keyEn="New mission" />}>
              <PlusOutlined style={{ fontSize: "2rem" }} />
            </Tooltip>
          }
        >
          <Translated keyEn="NEW" />
        </FloatButton>
      </FlexCol>
    </>
  )
}
